'use client';

import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';

const sourcingData = [
  { quarter: 'Q1 2025', domestic: 38, imported: 62 },
  { quarter: 'Q2 2025', domestic: 41, imported: 59 },
  { quarter: 'Q3 2025', domestic: 43, imported: 57 },
  { quarter: 'Q4 2025', domestic: 44, imported: 56 },
  { quarter: 'Q1 2026', domestic: 47, imported: 53 },
  { quarter: 'Q2 2026', domestic: 56, imported: 44 },
];

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-card p-3 min-w-[160px]">
      <p className="text-xs font-700 text-foreground mb-2">{label}</p>
      {payload.map((entry) => (
        <div key={`tooltip-${entry.name}`} className="flex items-center justify-between gap-4 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
            {entry.name}
          </span>
          <span className="font-700 tabular-nums">{entry.value}%</span>
        </div>
      ))}
    </div>
  );
}

export default function MarketCharts() {
  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Domestic vs Import Bar Chart */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-700 text-foreground">API Sourcing Mix — Domestic vs. Imported (%)</h3>
          <span className="text-xs text-muted-foreground">Q1 2025 – Q2 2026</span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Import Alert 66-40 expansion (May 2026) drove sharp shift to domestic sourcing in Q2 2026.
        </p>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sourcingData} barSize={28} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="quarter"
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                formatter={(value) => (
                  <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>{value}</span>
                )}
              />
              <ReferenceLine
                x="Q2 2026"
                stroke="var(--primary)"
                strokeDasharray="4 4"
                label={{ value: 'Alert 66-40', position: 'top', fontSize: 10, fill: 'var(--primary)' }}
              />
              <Bar dataKey="domestic" name="Domestic" fill="var(--primary)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="imported" name="Imported" fill="var(--muted)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}